import React, { FC, PropsWithChildren, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useIsDesktop } from "@hooks/layout";
import { useCountDownPlayback } from "@hooks/useCountDownPlayback";
import { TUseWavesurfer, useWavesurfer } from "@hooks/wafeform";

import { BottomPlayer } from "@components/BottomPlayer";
import { QuestionFlowDialog } from "@components/QuestionFlowDialog";
import { useQueryParam } from "use-query-params";

import { getTrack, PlayerContext, setTrack, TBottomPlayer } from "./utils";

export const PlayerLayout: FC<PropsWithChildren> = ({ children }) => {
  useTranslation(["questionFlow", "questionFlowMobile"]);

  const [data, setData] = useState<TBottomPlayer | null>(null);
  const [media, setMedia] = useState<HTMLMediaElement | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openQuestionFlow, setOpenQuestionFlow] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Guards the one-time mount hydration of a persisted bottom track.
  const hydratedRef = useRef(false);
  const [trackContainerReady, setTrackContainerReady] = useState(false);
  const [mediaControl, setMediaControl] = useState<TUseWavesurfer>({ containerRef });
  const { isUserCanPlay } = useCountDownPlayback();
  // `trackContainerReady` flips true once TrackComponent's main content
  // (which holds the `<div ref={containerRef}>`) renders. Pass it through
  // so useWavesurfer's effect re-runs at that moment and picks up the now-set
  // ref instead of skipping with `containerRef.current = null`.
  // onInteraction: clicking the big track-page waveform seeks (native) AND
  // starts playing from there; the shared media means any other player stops.
  useWavesurfer({
    ...mediaControl,
    isTrackPage: true,
    trackContainerReady,
    onInteraction: (ws) => {
      if (!isUserCanPlay) return setOpenDialog(true);
      ws.play().catch((e) => console.log(e, "track waveform click play error"));
    },
  });
  const track = getTrack();

  const [test] = useQueryParam("test");

  // Curated playlist pages: on DESKTOP play inline via each row's own waveform,
  // so hide the global bottom bar (kept MOUNTED — it owns the wavesurfer/audio
  // engine the row waveforms read). On MOBILE keep the bottom bar visible but in
  // `curated` mode (no download/heart).
  const { pathname } = useLocation();
  const isCuratedRoute = pathname.startsWith("/c/");
  const isDesktopLayout = useIsDesktop();
  const hidePlayerBar = isCuratedRoute && isDesktopLayout;

  const handleSetCurrentSong = (song: TBottomPlayer) => {
    setData(song);
  };

  const handleSetPlay = (isPlaying: boolean) => {
    // Functional update so we read the LATEST data, never a stale closure.
    // Critical for close: when the player is swiped away, wavesurfer fires a
    // `pause` event during teardown → setPlay(false). With the old stale-`data`
    // guard + unconditional spread, that resurrected the just-closed track
    // (reappearing paused). Returning `prev` untouched when it's already null
    // means a closed player can never be revived by a late play/pause event.
    setData((prev) => (prev && prev.id ? { ...prev, isPlaying } : prev));
  };

  useEffect(() => {
    if (test) {
      setOpenQuestionFlow(true);
    }
  }, [test]);

  useEffect(() => {
    if (!data) return;

    setTrack(data);
  }, [data?.id]);

  // Restore a persisted track ONCE on mount (so it resumes across reloads).
  // After that we must NOT auto-rehydrate from storage on every render —
  // otherwise closing the player right after it opens races the localStorage
  // write (the setTrack effect flushes after the close), and the player pops
  // back up on its own. Playing for a while hides the bug because that write
  // has long settled by the time you close.
  if (!hydratedRef.current) {
    hydratedRef.current = true;
    if (data === null && track) {
      setData({ ...track, isPlaying: false });
    }
  }

  const handleCloseTrack = () => {
    setData(null);
    setTrack(null);
    setMedia(null);
  };

  const handleOpenDialog = () => {
    setOpenDialog((prevState) => !prevState);
  };

  const handleMediaControl = (controls: Omit<TUseWavesurfer, "containerRef">) => {
    setMediaControl((prevState) => ({ ...prevState, ...controls }));
  };

  return (
    <PlayerContext.Provider
      value={{
        data,
        setData: handleSetCurrentSong,
        setPlay: handleSetPlay,
        setMedia,
        media,
        openLoginDialog: openDialog,
        setOpenLoginDialog: handleOpenDialog,
        handleMediaControl,
        containerRef,
        trackContainerReady,
        setTrackContainerReady,
      }}
    >
      {children}
      <div style={hidePlayerBar ? { opacity: 0, pointerEvents: "none" } : undefined}>
        <BottomPlayer curated={isCuratedRoute} handleCloseTrack={handleCloseTrack} isTrackActive={!!data} />
      </div>
      <QuestionFlowDialog
        open={openQuestionFlow}
        onClose={() => {
          setOpenQuestionFlow(false);
        }}
      />
    </PlayerContext.Provider>
  );
};
